/**
 * ProductDetails — description & vendor/type metadata
 */
import { useTranslation } from "react-i18next";

interface ProductDetailsProps {
  descriptionHtml?: string | null;
  vendor?: string | null;
  productType?: string | null;
  isShoe: boolean;
  isHeelInsoleAddon: boolean;
  hasMetalParts: boolean;
  sku?: string | null;
}

const SHOE_SAFETY_LINES = [
  "use",
  "water",
  "sole",
  "metal",
  "smallParts",
  "heat",
] as const;

const GOODS_SAFETY_LINES = ["metal", "water", "heat"] as const;

export function ProductDetails({
  descriptionHtml,
  vendor,
  productType,
  isShoe,
  isHeelInsoleAddon,
  hasMetalParts,
  sku,
}: Readonly<ProductDetailsProps>) {
  const { t } = useTranslation();

  return (
    <div className="mt-10 divide-y divide-border-6 rounded-3xl bg-white px-6 max-mobile:px-5">
      {/* Description */}
      {descriptionHtml && (
        <div className="py-6">
          <h2 className="mb-3 text-base font-bold text-text-1">
            {t("SingleProductPage.description")}
          </h2>
          <div
            className="blog-article-content prose prose-sm max-w-none text-text-3"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </div>
      )}

      {/* Shoe titles are the model code; other products carry it in the SKU (GPSR art. 19(c)). */}
      {!isShoe && sku && (
        <p className="py-6 text-small text-text-3">
          {t("SingleProductPage.productCode")}: {sku}
        </p>
      )}

      {/* Materials of the main shoe parts, the same for every model (owner, 4. 11. 2026). */}
      {isShoe && (
        <div className="py-6">
          <h2 className="mb-3 text-base font-bold text-text-1">
            {t("SingleProductPage.materials.title")}
          </h2>
          <dl className="space-y-1 text-small text-text-3">
            {(["upper", "lining", "sole"] as const).map((part) => (
              <div key={part} className="flex gap-1">
                <dt>{t(`SingleProductPage.materials.${part}`)}:</dt>
                <dd>{t(`SingleProductPage.materials.${part}Value`)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* GPSR (EU) 2023/988 art. 19(d): warnings and safety information.
          Leather goods get a shorter text; the heel insole add-on is fitted inside a shoe. */}
      {(isShoe || !isHeelInsoleAddon) && (
        <div className="py-6">
          <h2 className="mb-3 text-base font-bold text-text-1">
            {t("SingleProductPage.safety.title")}
          </h2>
          <ul className="list-disc space-y-1 pl-5 text-small text-text-3">
            {(isShoe ? SHOE_SAFETY_LINES : GOODS_SAFETY_LINES)
              .filter((line) => line !== "metal" || hasMetalParts)
              .map((line) => (
                <li key={line}>
                  {t(
                    `SingleProductPage.${isShoe ? "safety" : "safetyGoods"}.${line}`,
                  )}
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* GPSR (EU) 2023/988 art. 19(a): manufacturer name and postal and email address on every product offer. */}
      <div className="py-6">
        <h2 className="mb-3 text-base font-bold text-text-1">
          {t("SingleProductPage.manufacturer")}
        </h2>
        <address className="text-small text-text-3 not-italic leading-relaxed">
          HONOPONO j.d.o.o. (Postolar Varga)
          <br />
          Osječka ulica 148, 31207 Tenja,{" "}
          {t("SingleProductPage.manufacturerCountry")}
          <br />
          <a href="mailto:postolar.varga@gmail.com" className="hover:underline">
            postolar.varga@gmail.com
          </a>
        </address>
      </div>

      {/* Vendor / Type */}
      {/* {(vendor || productType) && (
        <div className="mt-6">
          <h2 className="text-text-1 mb-2 font-semibold">
            {t("SingleProductPage.details")}
          </h2>
          <dl className="text-text-3 space-y-1">
            {vendor && (
              <div className="flex gap-1">
                <dt className="font-medium">
                  {t("SingleProductPage.vendor")}:
                </dt>
                <dd>{vendor}</dd>
              </div>
            )}
            {productType && (
              <div className="flex gap-1">
                <dt className="font-medium">
                  {t("SingleProductPage.type")}:
                </dt>
                <dd>{productType}</dd>
              </div>
            )}
          </dl>
        </div>
      )} */}
    </div>
  );
}
